import {
  Card,
  Grid,
  MenuItem,
  Paper,
  Radio,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomStepper from "../../common/components/CustomStepper";
import Header from "../../common/components/Header";
import YellowButton from "../../common/components/YellowButton";
import { useBookingContext } from "../../context/BookingContext";
import { BookingContextActionEnum } from "../../enum";
import { StyledContainer } from "../step1/step1-styles";
import RoomSelection from "./components/RoomSelection";
import { StyledCard, StyledCard2, StyledTypographic } from "./step2-styles";

const Step2: FC = () => {
  const { state, dispatch } = useBookingContext();

  const [selectedValue, setSelectedValue] = useState<string>();
  const [roomConfiguration, setRoomConfiguration] = useState<Array<string>>([]);
  const navigate = useNavigate();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
  };

  const handleRoomConfiguration = (index: number, value: string) => {
    setRoomConfiguration((prev: Array<string>) => {
      const curr = [...prev];
      curr[index] = value;
      return curr;
    });
  };

  const selectedRooms = useMemo(() => {
    if (selectedValue) {
      return state.rooms[Number(selectedValue)];
    }
    return undefined;
  }, [selectedValue, state.rooms]);

  const length = useMemo(() => {
    if (state.startDate && state.endDate) {
      return Math.ceil(state.endDate.diff(state.startDate, "d", true));
    }
    return 0;
  }, [state.endDate, state.startDate]);

  useEffect(() => {
    if (selectedRooms) {
      setRoomConfiguration(selectedRooms.map(() => "Matrimonial"));
    } else {
      setRoomConfiguration([]);
    }
  }, [selectedRooms]);

  useEffect(() => {
    if (!state.selectedProgram) {
      navigate("/");
    }
    // Reset selected value if the selected room group is no longer available
    if (selectedValue && !state.rooms[Number(selectedValue)]) {
      setSelectedValue(undefined);
    }
  }, [navigate, state.selectedProgram, state.rooms, selectedValue]);

  const handleSubmit = () => {
    // DEBUG: Mostrar habitaciones seleccionadas
    console.log('Habitaciones seleccionadas:', selectedRooms);
    if (selectedRooms && selectedRooms.length > 0) {
      console.log('IDs de habitaciones seleccionadas:', selectedRooms.map(r => r.id));
    } else {
      console.log('No hay habitaciones seleccionadas');
    }
    // Ensure selectedRooms is defined before submitting
    if (selectedRooms) {
      dispatch({
        type: BookingContextActionEnum.SELECT_ROOMS,
        payload: { selectedRooms },
      });
      dispatch({
        type: BookingContextActionEnum.ADD_ROOM_CONFIG,
        payload: {
          room_configuration: roomConfiguration
            .slice(0, selectedRooms.length)
            .join(","),
        },
      });
      navigate("/datos-personales");
    }
  };

  return (
    <StyledContainer maxWidth={false} disableGutters>
      <Header opacity={0.8} />
      <CustomStepper activeStep={1} />
      <Grid container justifyContent='space-evenly' py={4}>
        {/* Columna Izquierda: Imagen y Fechas */}
        <Grid item xs={10} sm={5} marginBottom={4}>
          <img
            alt='habitación'
            src={`/images/${
              (selectedRooms && selectedRooms[0]?.image) ||
              "habitacion-clasica.jpg"
            }`}
            width='100%'
            style={{ display: "block" }}
          />
          <TableContainer
            component={Paper}
            sx={{ width: "100%", marginTop: "4px" }}
          >
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography fontWeight={600}>Noches</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>Check In</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>Check Out</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography>
                      {state.startDate && state.endDate
                        ? Math.ceil(
                            state.endDate.diff(state.startDate, "d", true)
                          )
                        : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={400}>
                      {state.startDate?.format("YYYY-MM-DD") || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={400}>
                      {state.endDate?.format("YYYY-MM-DD") || "-"}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Columna Derecha: Selección de Habitación */}
        <Grid item container xs={10} sm={5} rowSpacing={2}>
          {/* Tabla de Opciones Disponibles */}
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table aria-label='room-selection-table' size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell align='left' sx={{ width: "60%" }}>
                      <Typography fontWeight='bold'>
                        Opciones disponibles
                      </Typography>
                    </TableCell>
                    <TableCell align='left'>
                      <Typography fontWeight='bold'>
                        Precio (IVA incl.)
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell> {/* Cell for Radio */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.rooms &&
                    Object.values(state.rooms).map((roomGroup) => {
                      if (roomGroup.length > 0) {
                        const firstRoom = roomGroup[0];
                        const id = firstRoom.id;
                        const numberOfRooms = roomGroup.length;

                        // *** Lógica de deshabilitación para Casona (id 5) ***
                        const isCasona = id === 5;
                        const requiresMinimumRooms =
                          isCasona && numberOfRooms < 3; // Regla: Casona requiere >= 3 habitaciones

                        let name = `${numberOfRooms} ${
                          numberOfRooms > 1
                            ? firstRoom.pluralName
                            : firstRoom.name
                        }`;

                        const hasExtraBedGroup = roomGroup.some(
                          (r) => r.hasExtraBed
                        );

                        if (hasExtraBedGroup) {
                          const extraBedsCount = roomGroup.reduce(
                            (count, currentRoom) => {
                              return count + (currentRoom.hasExtraBed ? 1 : 0);
                            },
                            0
                          );
                          if (extraBedsCount > 0) {
                            name += ` + ${extraBedsCount} ${
                              extraBedsCount > 1
                                ? "Cama(s) adicional(es)"
                                : "Cama adicional"
                            }`;
                          }
                        }

                        const basePrice = roomGroup.reduce(
                          (sum, currentRoom) => sum + currentRoom.price,
                          0
                        );
                        const additionalBedPriceTotal = roomGroup.reduce(
                          (sum, currentRoom) =>
                            sum +
                            (currentRoom.hasExtraBed
                              ? currentRoom.additionalBedPrice
                              : 0),
                          0
                        );
                        const totalPrice =
                          (basePrice + additionalBedPriceTotal) * length;

                        return (
                          // Usamos React.Fragment porque podemos tener 2 filas por opción (opción + mensaje)
                          <React.Fragment key={id}>
                            <TableRow
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                                // Opcional: estilo visual si está deshabilitado
                                opacity: requiresMinimumRooms ? 0.5 : 1,
                                pointerEvents: requiresMinimumRooms
                                  ? "none"
                                  : "auto", // Deshabilita interacciones con la fila
                              }}
                            >
                              <TableCell sx={{ paddingY: "12px" }}>
                                <Typography>{name}</Typography>
                              </TableCell>
                              <TableCell sx={{ paddingY: "12px" }}>
                                <Typography>
                                  $
                                  {new Intl.NumberFormat("es-CL").format(
                                    totalPrice
                                  )}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ paddingY: "8px" }}>
                                <Radio
                                  checked={selectedValue === String(id)}
                                  onChange={handleChange}
                                  value={String(id)}
                                  sx={{
                                    color: "#FCD111",
                                    "&.Mui-checked": { color: "#F3C702" },
                                  }}
                                  // *** Aplicamos la deshabilitación aquí ***
                                  disabled={requiresMinimumRooms}
                                />
                              </TableCell>
                            </TableRow>
                            {/* Mensaje específico para id=5 (Suite Casona) */}
                            {isCasona ? ( // Mostramos el mensaje si es Casona, sin importar si está habilitado o no
                              <TableRow>
                                <TableCell
                                  colSpan={3}
                                  sx={{
                                    padding: "0 16px 8px 16px",
                                    borderBottom: "none",
                                  }}
                                >
                                  <StyledCard2 sx={{ margin: 0, padding: 1 }}>
                                    <StyledTypographic
                                      textAlign='center'
                                      variant='caption'
                                    >
                                      Habitaciones Suite Casona sólo se reserva
                                      por un mínimo de 3 habitaciones.
                                    </StyledTypographic>
                                  </StyledCard2>
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </React.Fragment>
                        );
                      }
                      return null;
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Tabla de Configuración de Habitaciones Seleccionadas */}
          {selectedRooms && selectedRooms.length > 0 && (
            <Grid item xs={12}>
              <TableContainer component={Paper}>
                <Table aria-label='room-configuration-table' size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell align='left' sx={{ width: "70%" }}>
                        <Typography fontWeight='bold'>Habitación</Typography>
                      </TableCell>
                      <TableCell align='left'>
                        <Typography fontWeight='bold'>Tipo Cama</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedRooms.map((room, index) => (
                      <TableRow
                        key={`${room.id}-${index}`} // Use a more unique key if possible
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell sx={{ paddingY: "12px" }}>
                          <Typography>{`${room.name} ${index + 1}`}</Typography>
                        </TableCell>
                        <TableCell sx={{ paddingY: "8px" }}>
                          <RoomSelection
                            disabled={!room.canSelectConfiguration}
                            // room={selectedValue} // selectedValue (group id) might not be needed here
                            index={index}
                            handleRoomConfiguration={handleRoomConfiguration}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          {/* Mensaje Condicional para Habitación Estándar (id=1) */}
          <Grid item xs={12}>
            {selectedValue === "1" ? (
              <StyledCard>
                <StyledTypographic textAlign='center' variant='body2'>
                 LAS HABITACIONES ESTÁNDAR ESTÁN UBICADAS SOBRE EL RESTAURANT, LO QUE IMPLICA RUIDO Y OLORES. CAPACIDAD MÁXIMA: 2 PERSONAS.
                </StyledTypographic>
              </StyledCard>
            ) : null}
          </Grid>

          {/* Botón Siguiente */}
          <Grid item xs={12} display='flex' justifyContent='end' marginTop={2}>
            <YellowButton
              handleSubmit={handleSubmit}
              text='Siguiente'
              disabled={!selectedRooms || selectedRooms.length === 0} // Mantén esta lógica para deshabilitar el botón general si no hay habitaciones seleccionadas.
            />
          </Grid>
        </Grid>
      </Grid>
    </StyledContainer>
  );
};

export default Step2;
